/**
 * EngagementContentTab.tsx — the ADMIN "Engagement content" library
 * (2026-09-23): the PDF guides the re-engagement emails and WhatsApp unlock
 * links serve, managed end to end — upload (create → signed PUT), publish,
 * retire, and the word edits the server's R28 sweep polices.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: engagement content
 * manager. CTO-approved design board, 2026-09-23 (ContentManager).
 *
 * ── WHY IT IS A RAIL SECTION AND NOT A CLINIC-SCOPED TAB ────────────────────
 * The library routes take no clinic; the guides serve the whole tenant's
 * unlock links (the pharmacy-section precedent: filing a tenant-wide surface
 * under `?clinic=` would imply a narrowing the server does not perform). It
 * registers in AdminPage's TABS + GLOBAL_TABS.
 *
 * ── THE UPLOAD IS TWO STEPS AND THE ROW EXISTS AFTER THE FIRST ──────────────
 * POST create → a DRAFT row + a 15-minute signed GCS PUT (content-type +
 * 1B–10MB bound into the signature) → the browser PUTs the PDF. A failed PUT
 * leaves a real draft whose publish 409s until the object lands, so the
 * dialog says exactly that and retries the PUT against the grant it already
 * holds (the WeightPhotoUploads discipline: re-PUT the held grant, never
 * re-create). `uploadUrl: null` is SERVER STUB MODE — the row still exists,
 * and the dialog says the upload needs a configured bucket rather than
 * pretending a PDF landed.
 *
 * ── REFUSALS ARE THE SERVER'S SENTENCES, AND THEY STAY VISIBLE ──────────────
 * The publish-before-upload 409 ("The PDF has not finished uploading…"), the
 * retired-never-republishes 409 and the R28 sweep's 400 are complete PHI-free
 * sentences rendered VERBATIM (R41 — the server is the control; a house
 * paraphrase would drift from the rule it describes). A dialog refusal stays
 * IN the open dialog (the close-as-lost lesson); a row-action refusal lands
 * in the page's notice slot, where it stays until the next action replaces
 * it.
 *
 * ── R28 ON A STAFF SURFACE ──────────────────────────────────────────────────
 * These titles and short messages land on PUBLIC unlock pages, so the SERVER
 * sweeps them at the write boundary. The portal's job is the helper line
 * (say up front never to name a medication) and the verbatim refusal — never
 * a client-side copy of the vocabulary, which could only drift shorter.
 */
import { useCallback, useEffect, useId, useState, type FormEvent } from 'react';
import { ApiError } from '../api/client';
import {
  ENGAGEMENT_PDF_MAX_BYTES,
  createEngagementContent,
  getEngagementContent,
  updateEngagementContent,
  uploadEngagementPdf,
} from '../api/engagement';
import type {
  EngagementContentCreateResult,
  EngagementContentItem,
  EngagementContentKind,
  EngagementContentStatus,
} from '../api/types';
import { formatUtcDate } from '../lib/format';
import { SectionTitle } from '../staff/ui';
import { ConfirmDialog, DialogShell } from '../ui/ConfirmDialog';
import { Menu, MenuItem } from '../ui/Menu';
import { DataTable, Td, Th } from '../ui/Table';
import { Alert, Badge, Button, ErrorState, Field, Help, Select, TextArea, TextInput, type AlertTone, type BadgeTone } from '../ui/kit';
import { TableSkeleton } from '../ui/skeletons';

const KIND_LABEL: Record<EngagementContentKind, string> = {
  tips: 'Tips',
  meal_plan: 'Meal plan',
  other: 'Other',
};

const STATUS_LABEL: Record<EngagementContentStatus, string> = {
  published: 'Published',
  draft: 'Draft',
  retired: 'Retired',
};

/** Published is the working state (positive); a draft is attention-pending;
 * retired is simply over (muted) — never danger, because nothing is wrong. */
const STATUS_TONE: Record<EngagementContentStatus, BadgeTone> = {
  published: 'positive',
  draft: 'pending',
  retired: 'muted',
};

/** The server's zod bounds on the words — the fields stop at them. */
const TITLE_MAX = 120;
const SHORT_MESSAGE_MAX = 300;

/**
 * A human size off the wire's `sizeBytes` — one decimal for MB (the design
 * board's "2.1 MB"), whole KB below that. Pure and exported for the spec.
 */
export function formatSizeBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(Math.round((bytes / (1024 * 1024)) * 10) / 10).toString()} MB`;
}

type Notice = { tone: AlertTone; text: string };

export function EngagementContentTab() {
  const [items, setItems] = useState<EngagementContentItem[] | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [showRetired, setShowRetired] = useState(false);
  /** The page's one notice slot — a refusal here STAYS until the next action. */
  const [notice, setNotice] = useState<Notice | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [editing, setEditing] = useState<EngagementContentItem | null>(null);
  const [retiring, setRetiring] = useState<EngagementContentItem | null>(null);
  const [publishBusy, setPublishBusy] = useState<string | null>(null);
  const retiredToggleId = useId();

  /**
   * The one read. `refresh` keeps the rows on screen while the re-read runs
   * (stale beats blank after a mutation); toggling the retired filter is an
   * `initial` read — page one of "guides" is not page one of "guides
   * including retired".
   */
  const load = useCallback(async (includeRetired: boolean, mode: 'initial' | 'refresh') => {
    if (mode === 'initial') setStatus('loading');
    try {
      const list = await getEngagementContent(includeRetired);
      setItems(list.items);
      setStatus('ready');
    } catch {
      setStatus((prev) => (mode === 'refresh' && prev === 'ready' ? 'ready' : 'error'));
    }
  }, []);
  useEffect(() => {
    void load(showRetired, 'initial');
  }, [load, showRetired]);

  /**
   * Publish is a one-press row action (no dialog: it makes nothing leave —
   * it only makes the guide OFFERABLE). Its 409s are the server's sentences,
   * shown verbatim in the notice slot, where they stay visible — the
   * upload-pending one is an instruction the admin acts on and presses again.
   */
  async function publish(item: EngagementContentItem) {
    if (publishBusy) return;
    setPublishBusy(item.id);
    setNotice(null);
    try {
      await updateEngagementContent(item.id, { publish: true });
      setNotice({ tone: 'success', text: `Published — “${item.title}” can now be sent to patients.` });
      void load(showRetired, 'refresh');
    } catch (err) {
      setNotice({
        tone: 'danger',
        text:
          err instanceof ApiError && err.detail
            ? err.detail
            : 'Sorry — we could not publish this. Nothing changed; try again.',
      });
    } finally {
      setPublishBusy(null);
    }
  }

  const rows = items ?? [];

  return (
    <div style={{ display: 'grid', gap: 'var(--gps-s-3)' }}>
      {/* The header row — title + explainer left, the controls right. Outside
          the loading/error branches, so a failed read never takes away the
          toggle or the upload door (the toolbar-in-all-three-states rule). */}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--gps-s-3)', flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0 }}>
          <SectionTitle>Engagement content</SectionTitle>
          <Help style={{ marginBottom: 0 }}>
            The PDFs your re-engagement emails and WhatsApp links unlock. Patients download them from
            their unlock page — nothing is ever attached to an email.
          </Help>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 'var(--gps-s-3)', flexWrap: 'wrap' }}>
          {/* The quiet retired reveal (the board's show-test-records idiom).
              ON sends the LITERAL `includeRetired=true`; OFF omits the param
              entirely — never a stringified boolean (api/engagement.ts). */}
          <label htmlFor={retiredToggleId} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: 'var(--gps-muted)', cursor: 'pointer' }}>
            <input
              id={retiredToggleId}
              type="checkbox"
              checked={showRetired}
              onChange={(e) => setShowRetired(e.target.checked)}
            />
            Show retired guides
          </label>
          {/* width:auto — buttonStyle() is width:100%, and a full-width child
              in a wrapping flex row takes the line to itself. */}
          <Button type="button" variant="primary" onClick={() => setUploadOpen(true)} style={{ width: 'auto' }}>
            Upload a guide…
          </Button>
        </div>
      </div>

      {notice ? (
        // A refusal alerts (the reader may have looked away); the quiet
        // successes announce politely. It stays until the next action.
        <Alert tone={notice.tone} role={notice.tone === 'danger' ? 'alert' : 'status'}>
          {notice.text}
        </Alert>
      ) : null}

      {status === 'loading' ? <TableSkeleton rows={4} columns={6} label="Loading the content library…" /> : null}
      {status === 'error' ? (
        <ErrorState message="We could not load the content library." onRetry={() => void load(showRetired, 'initial')} />
      ) : null}
      {status === 'ready' ? (
        rows.length === 0 ? (
          <Help style={{ margin: 0 }}>No guides yet — upload the first PDF to start the library.</Help>
        ) : (
          // Rendered IN THE SERVER'S ORDER — published, then draft, then
          // retired (the wire pins it; re-sorting here would be a second
          // opinion that drifts the day the server's ordering moves).
          <DataTable
            label="Engagement guides"
            head={
              <>
                <Th>Guide</Th>
                <Th>Kind</Th>
                <Th>Status</Th>
                <Th>Size</Th>
                <Th>Added</Th>
                <Th>
                  <span className="gps-sr-only">Actions</span>
                </Th>
              </>
            }
          >
            {rows.map((item) => (
              <tr key={item.id}>
                <Td>
                  <span style={{ fontWeight: 600 }}>{item.title}</span>
                  <span style={{ display: 'block', fontSize: '12.5px', color: 'var(--gps-muted)' }}>{item.shortMessage}</span>
                </Td>
                <Td>
                  <Badge tone="neutral">{KIND_LABEL[item.kind]}</Badge>
                </Td>
                <Td>
                  <Badge tone={STATUS_TONE[item.status]}>{STATUS_LABEL[item.status]}</Badge>
                </Td>
                <Td style={{ whiteSpace: 'nowrap' }}>{formatSizeBytes(item.sizeBytes)}</Td>
                <Td style={{ whiteSpace: 'nowrap' }}>{formatUtcDate(item.createdAt)}</Td>
                <Td style={{ textAlign: 'right' }}>
                  {/* A RETIRED row has no menu: nothing the server permits —
                      it never re-publishes, and its words are frozen with it.
                      An empty kebab would promise otherwise. */}
                  {item.status === 'retired' ? null : (
                    <Menu menuLabel={`Actions for ${item.title}`} triggerLabel={`Actions for ${item.title}`}>
                      {({ close }) => (
                        <>
                          {item.status === 'draft' ? (
                            <MenuItem
                              disabled={publishBusy !== null}
                              onSelect={() => {
                                close();
                                void publish(item);
                              }}
                            >
                              {publishBusy === item.id ? 'Publishing…' : 'Publish'}
                            </MenuItem>
                          ) : null}
                          <MenuItem
                            onSelect={() => {
                              close();
                              setEditing(item);
                            }}
                          >
                            Edit title &amp; message…
                          </MenuItem>
                          <MenuItem
                            onSelect={() => {
                              close();
                              setRetiring(item);
                            }}
                          >
                            Retire…
                          </MenuItem>
                        </>
                      )}
                    </Menu>
                  )}
                </Td>
              </tr>
            ))}
          </DataTable>
        )
      ) : null}

      {uploadOpen ? (
        <UploadGuideDialog
          onCancel={() => setUploadOpen(false)}
          // The draft row exists the moment the CREATE lands, whatever the
          // PUT then does — so the list refreshes on create, not on close.
          onRowCreated={() => void load(showRetired, 'refresh')}
          onDone={(done) => {
            setUploadOpen(false);
            setNotice(done);
            void load(showRetired, 'refresh');
          }}
        />
      ) : null}

      {editing ? (
        <EditGuideDialog
          item={editing}
          onCancel={() => setEditing(null)}
          onSaved={(saved) => {
            setEditing(null);
            setNotice(saved);
            void load(showRetired, 'refresh');
          }}
        />
      ) : null}

      {retiring ? (
        <RetireGuideDialog
          item={retiring}
          onCancel={() => setRetiring(null)}
          onRetired={(done) => {
            setRetiring(null);
            setNotice(done);
            void load(showRetired, 'refresh');
          }}
        />
      ) : null}
    </div>
  );
}

/* ── THE UPLOAD DIALOG ──────────────────────────────────────────────────────
 *
 * Create → signed PUT, with the honest client-side pre-checks in front (a
 * PDF only, ≤ 10 MB — the same ceiling the signature binds, so the message
 * and the refusal cannot disagree). Once the create has landed the WORD
 * fields deaden: the row holds those words now, and the Edit action owns
 * changing them — a dialog that kept them live would let the screen and the
 * record quietly diverge. A failed PUT keeps the dialog open and retries
 * against the SAME held grant.
 */
function UploadGuideDialog({
  onCancel,
  onRowCreated,
  onDone,
}: {
  onCancel: () => void;
  onRowCreated: () => void;
  onDone: (notice: Notice) => void;
}) {
  const [title, setTitle] = useState('');
  const [shortMessage, setShortMessage] = useState('');
  const [kind, setKind] = useState<EngagementContentKind>('tips');
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** The landed create — held so a failed PUT retries the SAME grant. */
  const [created, setCreated] = useState<EngagementContentCreateResult | null>(null);
  const titleId = useId();
  const messageId = useId();
  const kindId = useId();
  const fileId = useId();
  const errorId = useId();

  /** The honest pre-checks: a PDF, non-empty, at most the signed ceiling. */
  function pickFile(picked: File | undefined) {
    setFileError(null);
    setFile(null);
    if (!picked) return;
    if (picked.type !== 'application/pdf') {
      setFileError('Only a PDF can be uploaded here.');
      return;
    }
    if (picked.size < 1) {
      setFileError('That file is empty — pick the PDF you meant.');
      return;
    }
    if (picked.size > ENGAGEMENT_PDF_MAX_BYTES) {
      // Worded around the limit, not the file's rounded size — a 10.01 MB
      // file rounds to "10 MB", and "10 MB is over the 10 MB limit" reads as
      // a broken picker rather than an honest refusal.
      setFileError(`This PDF is larger than the ${formatSizeBytes(ENGAGEMENT_PDF_MAX_BYTES)} limit — pick a smaller file.`);
      return;
    }
    setFile(picked);
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (busy) return;
    const trimmedTitle = title.trim();
    const trimmedMessage = shortMessage.trim();
    if (!file) {
      setError('Choose the PDF to upload.');
      return;
    }
    if (trimmedTitle.length < 3 || trimmedMessage.length < 3) {
      setError('Give the guide a title and a WhatsApp message — at least 3 characters each.');
      return;
    }
    setBusy(true);
    setError(null);
    // Held locally so the catch can tell "the row exists" from "nothing was
    // saved" within THIS invocation (state is a render-old closure here).
    let grant = created;
    try {
      if (!grant) {
        grant = await createEngagementContent({
          title: trimmedTitle,
          shortMessage: trimmedMessage,
          kind,
          contentType: 'application/pdf',
          sizeBytes: file.size,
        });
        setCreated(grant);
        onRowCreated();
      }
      if (grant.uploadUrl === null) {
        // SERVER STUB MODE: the draft row exists; nothing was uploaded, and
        // saying anything softer would read as a PDF that landed.
        onDone({
          tone: 'warning',
          text: `“${trimmedTitle}” was created as a draft, but no storage bucket is configured on this environment — the PDF was not uploaded, and publishing will be refused until it is.`,
        });
        return;
      }
      // The PUT's length range echoes the WIRE's ceiling — the exact range
      // the server signed; the constant only stands in where the wire gave
      // null/absent (see uploadEngagementPdf's docblock).
      await uploadEngagementPdf(grant.uploadUrl, file, grant.maxBytes);
      onDone({ tone: 'success', text: `“${trimmedTitle}” uploaded — it is a draft until you publish it.` });
    } catch (err) {
      if (err instanceof ApiError && err.detail) {
        // The server's R28 sweep (or another refusal) — VERBATIM, dialog open.
        setError(err.detail);
      } else if (grant) {
        setError(
          'The guide was saved as a draft, but the PDF did not upload. Try the upload again — publishing is refused until the PDF has landed.',
        );
      } else {
        setError('Sorry — we could not create this guide. Nothing was saved; try again.');
      }
      setBusy(false);
    }
  }

  return (
    <DialogShell title="Upload a guide" onCancel={busy ? () => {} : onCancel} width="min(520px, 100%)">
      <form onSubmit={(e) => void submit(e)} aria-label="Upload a guide">
        <Field
          label="Title"
          htmlFor={titleId}
          hint="Shown to patients on their unlock page — never name a medication here."
        >
          <TextInput
            id={titleId}
            value={title}
            maxLength={TITLE_MAX}
            disabled={created !== null}
            onChange={(e) => setTitle(e.target.value)}
          />
        </Field>
        <Field
          label="WhatsApp short message"
          htmlFor={messageId}
          hint="The message patients may read on WhatsApp — an admin copies it with the unlock link added after it."
        >
          <TextArea
            id={messageId}
            value={shortMessage}
            maxLength={SHORT_MESSAGE_MAX}
            rows={2}
            disabled={created !== null}
            onChange={(e) => setShortMessage(e.target.value)}
          />
        </Field>
        <Field label="Kind" htmlFor={kindId}>
          <Select id={kindId} value={kind} disabled={created !== null} onChange={(e) => setKind(e.target.value as EngagementContentKind)}>
            <option value="tips">Tips</option>
            <option value="meal_plan">Meal plan</option>
            <option value="other">Other</option>
          </Select>
        </Field>
        <Field
          label="PDF file"
          htmlFor={fileId}
          hint={`A PDF, up to ${formatSizeBytes(ENGAGEMENT_PDF_MAX_BYTES)}.`}
          error={fileError ?? undefined}
        >
          <input
            id={fileId}
            type="file"
            accept="application/pdf"
            disabled={created !== null}
            onChange={(e) => pickFile(e.target.files?.[0])}
          />
        </Field>
        {error ? (
          <Alert tone="danger" role="alert" id={errorId} style={{ marginBottom: 'var(--gps-s-2)' }}>
            {error}
          </Alert>
        ) : null}
        <div style={{ display: 'flex', justifyContent: 'flex-start', gap: '8px', marginTop: 'var(--gps-s-2)' }}>
          <Button
            type="submit"
            variant="primary"
            disabled={busy}
            aria-describedby={error ? errorId : undefined}
            style={{ width: 'auto', minHeight: '44px', padding: '10px 18px', fontSize: '14.5px' }}
          >
            {busy ? 'Uploading…' : created ? 'Try the upload again' : 'Upload'}
          </Button>
          <Button type="button" variant="ghost" onClick={onCancel} disabled={busy} style={{ width: 'auto', minHeight: '44px', padding: '10px 18px', fontSize: '14.5px' }}>
            Cancel
          </Button>
        </div>
      </form>
    </DialogShell>
  );
}

/* ── THE EDIT DIALOG ────────────────────────────────────────────────────────
 *
 * Title + short message only — status moves live on the row menu. A 400 from
 * the server's R28 sweep is shown VERBATIM inside the still-open dialog with
 * the typed words kept (closing would throw away exactly what the admin now
 * needs to reword), and the save control points at the reason.
 */
function EditGuideDialog({
  item,
  onCancel,
  onSaved,
}: {
  item: EngagementContentItem;
  onCancel: () => void;
  onSaved: (notice: Notice) => void;
}) {
  const [title, setTitle] = useState(item.title);
  const [shortMessage, setShortMessage] = useState(item.shortMessage);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const titleId = useId();
  const messageId = useId();
  const errorId = useId();

  async function save(event: FormEvent) {
    event.preventDefault();
    if (busy) return;
    const trimmedTitle = title.trim();
    const trimmedMessage = shortMessage.trim();
    if (trimmedTitle.length < 3 || trimmedMessage.length < 3) {
      setError('The title and the WhatsApp message each need at least 3 characters.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await updateEngagementContent(item.id, { title: trimmedTitle, shortMessage: trimmedMessage });
      onSaved({ tone: 'success', text: `Saved — “${trimmedTitle}” was updated.` });
    } catch (err) {
      setError(
        err instanceof ApiError && err.detail
          ? err.detail
          : 'Sorry — we could not save this. Nothing changed; try again.',
      );
      setBusy(false);
    }
  }

  return (
    <DialogShell title={`Edit “${item.title}”`} onCancel={busy ? () => {} : onCancel} width="min(520px, 100%)">
      <form onSubmit={(e) => void save(e)} aria-label={`Edit “${item.title}”`}>
        <Field
          label="Title"
          htmlFor={titleId}
          hint="Shown to patients on their unlock page — never name a medication here."
        >
          <TextInput id={titleId} value={title} maxLength={TITLE_MAX} onChange={(e) => setTitle(e.target.value)} />
        </Field>
        <Field
          label="WhatsApp short message"
          htmlFor={messageId}
          hint="The message patients may read on WhatsApp — the unlock link is added after it."
        >
          <TextArea
            id={messageId}
            value={shortMessage}
            maxLength={SHORT_MESSAGE_MAX}
            rows={2}
            onChange={(e) => setShortMessage(e.target.value)}
          />
        </Field>
        {error ? (
          <Alert tone="danger" role="alert" id={errorId} style={{ marginBottom: 'var(--gps-s-2)' }}>
            {error}
          </Alert>
        ) : null}
        <div style={{ display: 'flex', justifyContent: 'flex-start', gap: '8px', marginTop: 'var(--gps-s-2)' }}>
          <Button
            type="submit"
            variant="primary"
            disabled={busy}
            aria-describedby={error ? errorId : undefined}
            style={{ width: 'auto', minHeight: '44px', padding: '10px 18px', fontSize: '14.5px' }}
          >
            {busy ? 'Saving…' : 'Save changes'}
          </Button>
          <Button type="button" variant="ghost" onClick={onCancel} disabled={busy} style={{ width: 'auto', minHeight: '44px', padding: '10px 18px', fontSize: '14.5px' }}>
            Cancel
          </Button>
        </div>
      </form>
    </DialogShell>
  );
}

/* ── THE RETIRE CONFIRM ─────────────────────────────────────────────────────
 *
 * BENIGN tone deliberately (ConfirmDialog's rule: danger is for the
 * consequence, not the importance) — retiring moves no money and reaches no
 * patient; links already sent keep working. The copy still states the one
 * irreversible half honestly: a retired guide is never re-published. A
 * refusal stays in the open dialog, verbatim.
 */
function RetireGuideDialog({
  item,
  onCancel,
  onRetired,
}: {
  item: EngagementContentItem;
  onCancel: () => void;
  onRetired: (notice: Notice) => void;
}) {
  const [busy, setBusy] = useState(false);
  /** `refusing: true` is a SERVER refusal (verbatim, confirm dead — no press
   * can change the answer); false is a transport fault, where the confirm
   * stays live because a retry genuinely can succeed. */
  const [refusal, setRefusal] = useState<{ text: string; refusing: boolean } | null>(null);
  const refusalId = useId();

  async function retire() {
    if (busy) return;
    setBusy(true);
    setRefusal(null);
    try {
      await updateEngagementContent(item.id, { retire: true });
      onRetired({ tone: 'success', text: `Retired — “${item.title}” will not be sent or offered to anyone new.` });
    } catch (err) {
      setRefusal(
        err instanceof ApiError && err.detail
          ? { text: err.detail, refusing: true }
          : { text: 'Sorry — we could not retire this. Nothing changed; try again.', refusing: false },
      );
      setBusy(false);
    }
  }

  return (
    <ConfirmDialog
      open
      title={`Retire “${item.title}”?`}
      confirmLabel={busy ? 'Retiring…' : 'Retire this guide'}
      busy={busy}
      confirmDisabled={refusal?.refusing === true}
      confirmDescribedBy={refusal ? refusalId : undefined}
      onCancel={onCancel}
      onConfirm={() => void retire()}
      body={
        <>
          {item.status === 'published' ? (
            <p style={{ margin: '0 0 var(--gps-s-2)' }}>
              It stops being sent or offered to anyone new. Links already sent keep working — patients who
              unlocked it keep their download.
            </p>
          ) : (
            <p style={{ margin: '0 0 var(--gps-s-2)' }}>
              This draft was never published, so no patient has it — retiring just tucks it away.
            </p>
          )}
          <p style={{ margin: 0 }}>A retired guide is never re-published. To offer it again, upload it as a new guide.</p>
          {refusal ? (
            <Alert tone="danger" role="alert" id={refusalId} style={{ marginTop: 'var(--gps-s-3)' }}>
              {refusal.text}
            </Alert>
          ) : null}
        </>
      }
    />
  );
}
